import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import * as XLSX from 'xlsx';

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface ImportedContact {
  nome: string;
  email: string;
  phone: string;
  tags: string;
  nome_do_agente?: string;
}

const normalizePhone = (phone: string): string => {
  let cleaned = phone.replace(/\D/g, '');

  if (cleaned.startsWith('55')) {
    cleaned = cleaned.substring(2);
  }

  if (cleaned.length === 11 || cleaned.length === 10) {
    return cleaned;
  }

  return cleaned;
};

const generatePhoneVariations = (phone: string): string[] => {
  const normalized = normalizePhone(phone);
  const variations: string[] = [];

  if (normalized.length === 11) {
    variations.push(`${normalized}@s.whatsapp.net`);
    const without9 = normalized.substring(0, 2) + normalized.substring(3);
    variations.push(`${without9}@s.whatsapp.net`);
  } else if (normalized.length === 10) {
    variations.push(`${normalized}@s.whatsapp.net`);
    const with9 = normalized.substring(0, 2) + '9' + normalized.substring(2);
    variations.push(`${with9}@s.whatsapp.net`);
  } else {
    variations.push(`${normalized}@s.whatsapp.net`);
  }

  return variations;
};

const ImportarContatos: React.FC = () => {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [previewData, setPreviewData] = useState<ImportedContact[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      alert('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }

    setFile(selectedFile);
    setValidationErrors([]);
    setImportSuccess(false);
    setPreviewData([]);

    await validateFile(selectedFile);
  };

  const validateFile = async (file: File) => {
    setValidating(true);
    const errors: ValidationError[] = [];
    const preview: ImportedContact[] = [];

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      if (jsonData.length < 2) {
        errors.push({
          row: 0,
          field: 'geral',
          message: 'Arquivo vazio ou sem dados'
        });
        setValidationErrors(errors);
        setValidating(false);
        return;
      }

      const headers = jsonData[0];
      const requiredHeaders = ['nome', 'email', 'phone', 'tags'];

      const normalizedHeaders = headers.map((h: string) =>
        h?.toString().toLowerCase().trim()
      );

      for (const required of requiredHeaders) {
        if (!normalizedHeaders.includes(required)) {
          errors.push({
            row: 0,
            field: required,
            message: `Coluna "${required}" não encontrada no arquivo`
          });
        }
      }

      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidating(false);
        return;
      }

      const nomeIndex = normalizedHeaders.indexOf('nome');
      const emailIndex = normalizedHeaders.indexOf('email');
      const phoneIndex = normalizedHeaders.indexOf('phone');
      const tagsIndex = normalizedHeaders.indexOf('tags');
      const nomeDoAgenteIndex = normalizedHeaders.indexOf('nome_do_agente');

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (!row || row.length === 0 || !row.some(cell => cell)) {
          continue;
        }

        const nome = row[nomeIndex]?.toString().trim() || '';
        const email = row[emailIndex]?.toString().trim() || '';
        const phone = row[phoneIndex]?.toString().trim() || '';
        const tags = row[tagsIndex]?.toString().trim() || '';
        const nomeDoAgente = nomeDoAgenteIndex >= 0 ? (row[nomeDoAgenteIndex]?.toString().trim() || '') : '';

        if (!nome) {
          errors.push({
            row: i + 1,
            field: 'nome',
            message: 'Nome é obrigatório'
          });
        }

        if (!phone) {
          errors.push({
            row: i + 1,
            field: 'phone',
            message: 'Telefone é obrigatório'
          });
        } else {
          const cleanPhone = phone.replace(/\D/g, '');
          if (cleanPhone.length < 10) {
            errors.push({
              row: i + 1,
              field: 'phone',
              message: 'Telefone inválido (mínimo 10 dígitos)'
            });
          }
        }

        if (email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          errors.push({
            row: i + 1,
            field: 'email',
            message: 'E-mail inválido'
          });
        }

        if (errors.filter(e => e.row === i + 1).length === 0) {
          preview.push({
            nome,
            email,
            phone,
            tags,
            nome_do_agente: nomeDoAgente
          });
        }
      }

      setValidationErrors(errors);
      setPreviewData(preview.slice(0, 5));

    } catch (error) {
      console.error('Erro ao ler arquivo:', error);
      errors.push({
        row: 0,
        field: 'geral',
        message: 'Erro ao processar arquivo. Verifique o formato.'
      });
      setValidationErrors(errors);
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!file || !user?.account_id || validationErrors.length > 0) {
      return;
    }

    setImporting(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      const headers = jsonData[0].map((h: string) => h?.toString().toLowerCase().trim());
      const nomeIndex = headers.indexOf('nome');
      const emailIndex = headers.indexOf('email');
      const phoneIndex = headers.indexOf('phone');
      const tagsIndex = headers.indexOf('tags');
      const nomeDoAgenteIndex = headers.indexOf('nome_do_agente');

      if (!supabase) {
        alert('Erro ao conectar com o banco de dados');
        setImporting(false);
        return;
      }

      const { data: assistantsData } = await supabase
        .from('account_assistants')
        .select('id, name')
        .eq('account_id', user.account_id);

      const assistantMap = new Map<string, string>();
      if (assistantsData) {
        for (const assistant of assistantsData) {
          const normalizedName = assistant.name.toLowerCase().trim();
          assistantMap.set(normalizedName, assistant.id);
        }
      }

      const allPhoneVariations: string[] = [];
      const phoneToRowData = new Map();

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (!row || row.length === 0 || !row.some(cell => cell)) {
          continue;
        }

        const nome = row[nomeIndex]?.toString().trim();
        const email = row[emailIndex]?.toString().trim() || null;
        const phone = row[phoneIndex]?.toString().trim();
        const tagsStr = row[tagsIndex]?.toString().trim() || '';
        const nomeDoAgente = nomeDoAgenteIndex >= 0 ? (row[nomeDoAgenteIndex]?.toString().trim() || '') : '';

        if (!nome || !phone) continue;

        const cleanPhone = phone.replace(/\D/g, '');
        if (cleanPhone.length < 10) continue;

        const variations = generatePhoneVariations(cleanPhone);
        allPhoneVariations.push(...variations);

        const tagsList = tagsStr
          .split(',')
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0);

        let assistantId: string | null = null;
        if (nomeDoAgente) {
          const normalizedAgentName = nomeDoAgente.toLowerCase().trim();
          assistantId = assistantMap.get(normalizedAgentName) || null;
        }

        phoneToRowData.set(cleanPhone, {
          nome,
          email,
          phone: cleanPhone,
          tags: tagsList,
          variations,
          assistantId
        });
      }

      if (phoneToRowData.size === 0) {
        alert('Nenhum contato válido encontrado no arquivo');
        setImporting(false);
        return;
      }

      const { data: existingContacts } = await supabase
        .from('account_contacts')
        .select('id, jid, name, phone')
        .eq('account_id', user.account_id)
        .in('jid', allPhoneVariations);

      const existingJidMap = new Map();
      if (existingContacts) {
        for (const contact of existingContacts) {
          existingJidMap.set(contact.jid, contact);
        }
      }

      const contactsToInsert = [];
      const contactsToUpdate = [];

      for (const [cleanPhone, rowData] of phoneToRowData.entries()) {
        let existingContact = null;

        for (const variation of rowData.variations) {
          if (existingJidMap.has(variation)) {
            existingContact = existingJidMap.get(variation);
            break;
          }
        }

        const contactData = {
          account_id: user.account_id,
          name: rowData.nome,
          email: rowData.email,
          phone: rowData.phone,
          is_group: false,
          updated_at: new Date().toISOString(),
          tags: rowData.tags.length > 0 ? rowData.tags : null,
          account_assistant_id: rowData.assistantId || null,
          assistant_is_active: rowData.assistantId ? true : false
        };

        if (existingContact) {
          contactsToUpdate.push({
            id: existingContact.id,
            ...contactData
          });
        } else {
          const primaryJid = rowData.variations[0];
          contactsToInsert.push({
            jid: primaryJid,
            created_at: new Date().toISOString(),
            last_message_at: new Date().toISOString(),
            ...contactData
          });
        }
      }

      let insertedCount = 0;
      let updatedCount = 0;

      if (contactsToInsert.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('account_contacts')
          .insert(contactsToInsert)
          .select('id');

        if (insertError) {
          console.error('Erro ao inserir contatos:', insertError);
          alert(`Erro ao inserir contatos: ${insertError.message}`);
          setImporting(false);
          return;
        }

        insertedCount = insertedData?.length || 0;
      }

      if (contactsToUpdate.length > 0) {
        for (const contact of contactsToUpdate) {
          const { error: updateError } = await supabase
            .from('account_contacts')
            .update(contact)
            .eq('id', contact.id);

          if (updateError) {
            console.error('Erro ao atualizar contato:', updateError);
          } else {
            updatedCount++;
          }
        }
      }

      const totalCount = insertedCount + updatedCount;

      setImportedCount(totalCount);
      setImportSuccess(true);
      setFile(null);
      setPreviewData([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Erro na importação:', error);
      alert('Erro ao importar contatos. Tente novamente.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['nome', 'email', 'phone', 'tags', 'nome_do_agente'],
      ['João Silva', 'joao@example.com', '11999999999', 'cliente, vip', 'Assistente Vendas'],
      ['Maria Santos', 'maria@example.com', '11888888888', 'lead', 'Suporte Técnico'],
      ['Pedro Oliveira', '', '11777777777', 'cliente', '']
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contatos');

    ws['!cols'] = [
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 30 },
      { wch: 25 }
    ];

    XLSX.writeFile(wb, 'modelo_importacao_contatos.xlsx');
  };

  return (
    <div className="w-full flex h-[calc(100dvh-110px)] flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[26px] sm:text-[30px] font-semibold text-neutral-50">
          Importar Contatos
        </h2>
        <Button
          onClick={() => window.history.back()}
          variant="ghost"
          className="text-neutral-300 hover:text-neutral-50"
        >
          <X className="w-5 h-5 mr-2" />
          Voltar
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          {importSuccess && (
            <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-green-300 font-semibold mb-1">Importação concluída!</h3>
                <p className="text-green-200 text-sm">
                  {importedCount} contato(s) importado(s) com sucesso.
                </p>
              </div>
            </div>
          )}

          <div className="bg-neutral-800 rounded-lg p-6 border border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-50 mb-4">1. Baixar Modelo</h3>
            <p className="text-neutral-300 text-sm mb-4">
              Faça o download do modelo Excel com as colunas corretas para importação.
            </p>
            <Button
              onClick={downloadTemplate}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar Modelo
            </Button>
          </div>

          <div className="bg-neutral-800 rounded-lg p-6 border border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-50 mb-4">2. Selecionar Arquivo</h3>
            <p className="text-neutral-300 text-sm mb-4">
              Selecione o arquivo Excel (.xlsx ou .xls) preenchido com os dados dos contatos.
            </p>

            <div className="border-2 border-dashed border-neutral-600 rounded-lg p-8 text-center hover:border-neutral-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FileSpreadsheet className="w-12 h-12 text-neutral-400 mb-3" />
                {file ? (
                  <div className="text-neutral-300">
                    <p className="font-semibold">{file.name}</p>
                    <p className="text-sm text-neutral-400 mt-1">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-neutral-300 font-medium mb-1">
                      Clique para selecionar arquivo
                    </p>
                    <p className="text-neutral-400 text-sm">
                      ou arraste e solte aqui
                    </p>
                  </>
                )}
              </label>
            </div>

            {validating && (
              <div className="mt-4 flex items-center justify-center gap-2 text-neutral-300">
                <div className="w-4 h-4 border-2 border-neutral-300 border-t-transparent rounded-full animate-spin" />
                <span>Validando arquivo...</span>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="mt-4 bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-red-300 font-semibold mb-2">
                      Erros de validação ({validationErrors.length})
                    </h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {validationErrors.slice(0, 10).map((error, index) => (
                        <p key={index} className="text-red-200 text-sm">
                          Linha {error.row} - {error.field}: {error.message}
                        </p>
                      ))}
                      {validationErrors.length > 10 && (
                        <p className="text-red-200 text-sm italic">
                          ... e mais {validationErrors.length - 10} erro(s)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {previewData.length > 0 && validationErrors.length === 0 && (
              <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h4 className="text-green-300 font-semibold mb-3">
                  Preview (primeiros 5 registros)
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-600">
                        <th className="text-left text-neutral-300 pb-2 pr-4">Nome</th>
                        <th className="text-left text-neutral-300 pb-2 pr-4">E-mail</th>
                        <th className="text-left text-neutral-300 pb-2 pr-4">Telefone</th>
                        <th className="text-left text-neutral-300 pb-2 pr-4">Tags</th>
                        <th className="text-left text-neutral-300 pb-2">Agente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((contact, index) => (
                        <tr key={index} className="border-b border-neutral-700">
                          <td className="text-neutral-200 py-2 pr-4">{contact.nome}</td>
                          <td className="text-neutral-200 py-2 pr-4">{contact.email || '-'}</td>
                          <td className="text-neutral-200 py-2 pr-4">{contact.phone}</td>
                          <td className="text-neutral-200 py-2 pr-4">{contact.tags || '-'}</td>
                          <td className="text-neutral-200 py-2">{contact.nome_do_agente || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="bg-neutral-800 rounded-lg p-6 border border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-50 mb-4">3. Importar</h3>
            <p className="text-neutral-300 text-sm mb-4">
              Após validar o arquivo, clique no botão abaixo para importar os contatos.
            </p>
            <Button
              onClick={handleImport}
              disabled={!file || validationErrors.length > 0 || importing || validating}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Contatos
                </>
              )}
            </Button>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Informações importantes:</h4>
            <ul className="text-blue-200 text-sm space-y-1 list-disc list-inside">
              <li>O arquivo deve estar no formato Excel (.xlsx ou .xls)</li>
              <li>As colunas obrigatórias são: nome, email, phone, tags</li>
              <li>A coluna <strong>nome_do_agente</strong> é opcional e deve conter o nome exato do assistente</li>
              <li>Se o nome do agente for fornecido e encontrado, o assistente será ativado automaticamente</li>
              <li>Contatos sem nome de agente ou com agente não encontrado serão importados sem agente associado</li>
              <li>Nomes de agentes não diferenciam maiúsculas de minúsculas</li>
              <li>O telefone deve conter apenas números (DDD + número)</li>
              <li>As tags devem ser separadas por vírgula</li>
              <li>Contatos duplicados serão atualizados</li>
              <li>A data de cadastro e atualização será registrada automaticamente</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarContatos;
